import isNil from 'lodash/isNil';
import {ReactNode, useEffect} from 'react';
import {OnlyInLoginChecker} from '../../../../common/checkers/OnlyInLoginChecker';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {ContentHomeUpperTab} from '../../../../design-system/organisms/ContentHomeUpperTab/ContentHomeUpperTab';
import {SaleMethod} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {ReactQueryFetcher} from '../../../../reactQuery/ReactQueryFetcher';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {
  useContentCheckFreeTicketQuery,
  useContentMyTicketQuery,
} from '../../../../wrapper/ticketWrapper';
import {REFRESH_MY_TICKET_EVENT} from '../constant';
import {ContentFreeTicketCheckerHeadless} from '../headless/ContentFreeTicketCheckerHeadless';
import {ContentSaleStateCheckerHeadless} from '../headless/ContentSaleStateCheckerHeadless';
import {useContentHomeTabList} from '../hook/useContentHomeTabList';
import {useInitializeScrollTop} from '../hook/useInitializeScrollTop';
import {setIsEnteredContentHome} from '../state/contentHomeSlice';
import {ContentHomeTabType} from '../type';
import {ContentCommentMobileContainer} from './ContentCommentMobileContainer';
import {ContentDetailTabContainer} from './ContentDetailTabContainer';
import {ContentHomeProductTabMobileContainer} from './ContentHomeProductTabMobileContainer';
import {ContentMainTicketActionContainer} from './ContentMainTicketActionContainer';
import {ContentNoticeTabMobileContainer} from './ContentNoticeTabMobileContainer';
import {ContentOverviewContainer} from './ContentOverviewContainer';
import {ContentPreviewMobileContainer} from './ContentPreviewMobileContainer';
import {ContentReceiveFreeTicketContainer} from './ContentReceiveFreeTicketContainer';

export interface ContentMainMobileContainerProps {
  seriesId: number;
  tabType: ContentHomeTabType;
  isWebView: boolean;
}

export function ContentMainMobileContainer({
  seriesId,
  tabType,
  isWebView,
}: ContentMainMobileContainerProps) {
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;
  const preview = data?.content?.serviceProperty?.preview;
  const isCashTypeHome = data?.content?.saleMethod === SaleMethod.Cash;

  const tabList = useContentHomeTabList({seriesId});
  const dispatch = useTypedDispatch();
  const {refetch: refetchMyTicket} = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: false}
  );

  useEffect(() => {
    dispatch(setIsEnteredContentHome({seriesId, isEntered: true}));
    return () => {
      dispatch(setIsEnteredContentHome({seriesId, isEntered: false}));
    };
  }, [dispatch, seriesId]);

  useEffect(() => {
    const refetch = () => refetchMyTicket();

    window.addEventListener(REFRESH_MY_TICKET_EVENT, refetch);
    return () => {
      window.removeEventListener(REFRESH_MY_TICKET_EVENT, refetch);
    };
  }, [refetchMyTicket]);

  useInitializeScrollTop(tabType);

  return (
    <div className="flex flex-1 flex-col">
      <ContentSaleStateCheckerHeadless seriesId={seriesId}>
        <OnlyInLoginChecker>
          {!!data && !isCashTypeHome && (
            <>
              <ReactQueryFetcher
                reactQuery={useContentCheckFreeTicketQuery}
                variables={{seriesId}}
                ignoreLoading={true}
                ignoreFetching={true}
                options={{throwOnError: false}}
                // free_check API 는 옵셔널한 API 라 어떤 에러가 발생하든 무시합니다. (ios, aos 동일)
                onError={() => {}}
              >
                {({contentCheckFreeTicket}) => (
                  <ContentFreeTicketCheckerHeadless
                    seriesId={seriesId}
                    contentCheckFreeTicket={contentCheckFreeTicket}
                  >
                    <ContentReceiveFreeTicketContainer
                      seriesId={seriesId}
                      contentCheckFreeTicket={contentCheckFreeTicket}
                    />
                  </ContentFreeTicketCheckerHeadless>
                )}
              </ReactQueryFetcher>
              <ReactQueryFetcher
                reactQuery={useContentMyTicketQuery}
                variables={{seriesId, includeWaitfree: true}}
                ignoreLoading={true}
                ignoreFetching={true}
              />
            </>
          )}
        </OnlyInLoginChecker>
      </ContentSaleStateCheckerHeadless>
      <ContentOverviewContainer seriesId={seriesId} />
      <TabPositionControl
        depth={1}
        isMobile={true}
        isWebView={isWebView}
        disabled={false}
      >
        <ContentHomeUpperTab
          isMobile={true}
          tabItems={tabList.map(tab => ({
            ...tab,
            hasNewIcon: tab.hasNewIcon,
          }))}
          currentTabItemId={tabType}
          className="px-0"
        />
      </TabPositionControl>
      {MobileContentHomeTabComponentMap[tabType](
        seriesId,
        preview?.item?.productId
      )}
      <ContentMainTicketActionContainer seriesId={seriesId} tabType={tabType} />
    </div>
  );
}

const MobileContentHomeTabComponentMap: {
  [key in ContentHomeTabType]: (
    seriesId: number,
    productId?: number
  ) => ReactNode;
} = {
  [ContentHomeTabType.overview]: seriesId => (
    <ContentHomeProductTabMobileContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.preview]: (seriesId, productId) => {
    if (isNil(productId)) {
      return <ContentHomeProductTabMobileContainer seriesId={seriesId} />;
    }
    return (
      <ContentPreviewMobileContainer
        seriesId={seriesId}
        productId={productId}
      />
    );
  },
  [ContentHomeTabType.product]: seriesId => (
    <ContentHomeProductTabMobileContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.about]: seriesId => (
    <ContentDetailTabContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.notice]: seriesId => (
    <ContentNoticeTabMobileContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.comment]: seriesId => (
    <ContentCommentMobileContainer seriesId={seriesId} />
  ),
};
